<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { debounce } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

// v-model: display_id de la conversación ligada. Reusa la búsqueda nativa de
// conversaciones (store conversationSearch) — no hay un picker listo en la app.
const selected = defineModel({ type: [Number, String], default: '' });

const props = defineProps({
  displayLabel: { type: String, default: '' },
});

const { t } = useI18n();
const store = useStore();

const results = useMapGetter('conversationSearch/getConversationRecords');

const open = ref(false);
const query = ref('');

const buttonLabel = computed(() => {
  if (selected.value) {
    return props.displayLabel || `#${selected.value}`;
  }
  return t('TICKETS.DIALOG.CONVERSATION_PLACEHOLDER');
});

const runSearch = debounce(q => {
  if (q && q.length > 1) {
    store.dispatch('conversationSearch/conversationSearch', { q });
  }
}, 300);

const onInput = () => runSearch(query.value);

const select = conversation => {
  selected.value = conversation.id;
  open.value = false;
  query.value = '';
};

const clear = () => {
  selected.value = '';
};
</script>

<template>
  <OnClickOutside @trigger="open = false">
    <div class="relative w-full">
      <Button
        slate
        outline
        trailing-icon
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        :label="buttonLabel"
        class="justify-between w-full !px-3 font-normal"
        @click="open = !open"
      />
      <button
        v-if="selected"
        type="button"
        class="absolute -translate-y-1/2 right-9 top-1/2 text-n-slate-10 hover:text-n-slate-12 i-lucide-x size-4"
        @click.stop="clear"
      />

      <div
        v-if="open"
        class="absolute z-[9999] w-full p-2 mt-1 border rounded-lg shadow-lg bg-n-alpha-3 backdrop-blur-[100px] border-n-strong"
      >
        <input
          v-model="query"
          :placeholder="t('TICKETS.DIALOG.CONVERSATION_SEARCH')"
          class="w-full px-2.5 py-2 mb-1 text-sm rounded-md outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          @input="onInput"
        />
        <ul class="flex flex-col max-h-56 overflow-y-auto">
          <li
            v-for="conversation in results"
            :key="conversation.id"
            class="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-n-alpha-2"
            @click="select(conversation)"
          >
            <span class="text-xs text-n-slate-10 tabular-nums">
              #{{ conversation.id }}
            </span>
            <span class="text-sm truncate text-n-slate-12">
              {{ conversation.contact?.name || conversation.meta?.sender?.name }}
            </span>
          </li>
          <li
            v-if="!results.length"
            class="px-2 py-2 text-sm text-n-slate-11"
          >
            {{ t('TICKETS.DIALOG.CONVERSATION_EMPTY') }}
          </li>
        </ul>
      </div>
    </div>
  </OnClickOutside>
</template>
